<script>
    import { monsterGrowthFundConfig, playerMonsterGrowthFund, playerStateLoaded, claimedMonsterGrowthFundPrizes, defeatedMonsterLevels, storePacks } from '../../stores';
    import { _ } from 'svelte-i18n';
    import { collect_monster_growth_fund_route } from '../../constants';
    import { onMount, getContext } from 'svelte';
    import { goTo } from '../../utils.js';
    import Starburst from '../Starburst.svelte';
    import '../../styles/atlases/GameItems.css';
    import greenCheckmark from '../../assets/gold_rush/green_checkmark.png?format=webp;jpg';

    const { buyPack: buyPackFromContext } = getContext('buyPack');

    let claimingLevel = $state(null);
    let levelErrors = $state({});

    let partisanPack = $derived(
        $storePacks.find(pack => pack.internalId === 443) ?? null
    );

    // Pack 443 grants the one-time monsterGrowthFund perk; the backend cannot record it twice,
    // so a second purchase charges the player and never validates. Offer Unlock only when player
    // state has actually loaded and says the fund is not owned (fail closed on an unknown state).
    let canUnlock = $derived($playerStateLoaded && !$playerMonsterGrowthFund);

    function getMonsterIcon(index) {
        if (index < 2)  return '/images/advancement-rewards/partisan_ogre.png';
        if (index < 6)  return '/images/advancement-rewards/partisan_wraith.png';
        return '/images/advancement-rewards/partisan_dragon.png';
    }

    function unlockPack() {
        if (!partisanPack) return;
        buyPackFromContext({
            detail: {
                storePack: partisanPack,
                packDetails: { title: $_('MonsterProgressPack20Title'), description: '', subTitle: '' },
            }
        });
    }

    let sortedLevels = $derived(
        $monsterGrowthFundConfig
            ? Object.entries($monsterGrowthFundConfig.levels).sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
            : []
    );

    function getPriceString(pack) {
        if (!pack?.paymentHub?.price || pack.paymentHub.price.amount === 'missing') return '';
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: pack.paymentHub.price.currency,
        }).format(pack.paymentHub.price.amount / 100);
    }

    async function claim(level) {
        if (claimingLevel !== null) return;
        claimingLevel = level;
        delete levelErrors[level];
        levelErrors = { ...levelErrors };
        try {
            const response = await fetch(collect_monster_growth_fund_route, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ level: parseInt(level) }),
            });
            if (!response.ok) {
                let msg;
                try { const d = await response.json(); msg = d?.message || d?.error || `Error ${response.status}`; }
                catch { msg = `Error ${response.status}`; }
                levelErrors = { ...levelErrors, [level]: msg };
                setTimeout(() => { delete levelErrors[level]; levelErrors = { ...levelErrors }; }, 3000);
                return;
            }
            claimedMonsterGrowthFundPrizes.update(arr => [...arr, parseInt(level)]);
        } catch (e) {
            levelErrors = { ...levelErrors, [level]: 'Request failed' };
            setTimeout(() => { delete levelErrors[level]; levelErrors = { ...levelErrors }; }, 3000);
        } finally {
            claimingLevel = null;
        }
    }

    onMount(() => {
        window.scrollTo(0, 0);
        goTo('/supplies/advancementRewards?tab=partisanProgress', true);
    });
</script>

<div class="pp-container">
    <div class="hero">
        <div class="hero-content">
            <div class="hero-text">
                <h1>{$_('MonsterProgressPack20Title')}</h1>
                <p>{$_('MonsterProgressPack20Description1')}</p>
            </div>
            <div class="hero-action">
                {#if canUnlock}
                    <div class="unlock-wrap">
                        {#if partisanPack?.discount > 0}
                            <div class="starburst-pos">
                                <Starburst label="Discount">
                                    <div>-{partisanPack.discount}%</div>
                                </Starburst>
                            </div>
                        {/if}
                        <button
                            class="button primary orange unlock-btn"
                            disabled={!partisanPack}
                            onclick={unlockPack}
                        >
                            <span class="unlock-label">{$_('Unlock')}</span>
                            <span class="unlock-price">{getPriceString(partisanPack)}</span>
                        </button>
                    </div>
                {/if}
            </div>
        </div>
    </div>

    {#if !$monsterGrowthFundConfig}
        <div class="empty-state">{$_('PartisanProgressUnavailable')}</div>
    {:else}
        <div class="rows-list">
            {#each sortedLevels as [level, { gold, monster }], i (level)}
                {@const claimed = $claimedMonsterGrowthFundPrizes.includes(parseInt(level))}
                {@const defeated = $defeatedMonsterLevels.includes(parseInt(level))}
                {@const claimable = $playerMonsterGrowthFund && defeated && !claimed}
                {@const locked = !$playerMonsterGrowthFund || !defeated}
                <div class="reward-row" class:claimed style="animation-delay:{i*40}ms">
                    <div class="row-icon">
                        <div class="icon-frame" class:icon-claimable={claimable} class:icon-claimed={claimed} class:icon-locked={locked && !claimed}>
                            <img src={getMonsterIcon(i)} alt="Monster {i+1}" class="monster-img" class:img-locked={locked && !claimed} />
                            {#if claimed}
                                <img src={greenCheckmark[0]} alt="Claimed" class="claimed-overlay" />
                            {:else if locked}
                                <img src="/images/advancement-rewards/lock.png" alt="Locked" class="lock-overlay" />
                            {/if}
                        </div>
                    </div>
                    <div class="row-text">
                        <span class="row-gold">Get {parseInt(gold).toLocaleString()} {$_('Gold')}<span class="gold-icon"><i class="gold_l"></i></span></span>
                        <span class="row-req">{$_('DefeatMonsterLevel', { values: { LEVEL: level, MONSTER: monster ? $_(monster.charAt(0).toUpperCase() + monster.slice(1)) : '' } })}</span>
                    </div>
                    <div class="row-action">
                        {#if claimed}
                            <div class="claimed-text">{$_('Claimed')}</div>
                        {:else if claimable}
                            <button
                                class="button primary green action-btn"
                                class:loading={claimingLevel === level}
                                disabled={claimingLevel === level}
                                onclick={() => claim(level)}
                            >
                                {claimingLevel === level ? '…' : $_('Claim')}
                            </button>
                        {:else}
                            <button class="button primary action-btn btn-locked" disabled>{$_('Claim')}</button>
                        {/if}
                        {#if levelErrors[level]}
                            <span class="error-msg">{levelErrors[level]}</span>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style>
    .pp-container {
        max-width: 680px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
    }

    /* ── Hero ── */
    .hero {
        position: relative;
        width: 100%;
        height: 200px;
        background-image: url('/images/permanent_monsters_event_bg.png');
        background-size: cover;
        background-position: center center;
        overflow: hidden;
    }

    .hero::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, rgba(30,10,0,0.15) 0%, rgba(30,10,0,0.55) 50%, rgba(10,3,0,0.9) 100%);
    }

    .hero-content {
        position: absolute;
        inset: 0;
        z-index: 1;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        padding: 16px 18px;
        gap: 12px;
    }

    .starburst-pos {
        position: absolute;
        top: -12px;
        right: -5px;
        z-index: 2;
        transform: scale(0.72);
        transform-origin: top right;
    }

    .hero-text h1 {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 2.4rem;
        font-weight: 900;
        color: #fff;
        text-transform: uppercase;
        text-shadow: 0 2px 8px rgba(0,0,0,0.9), 0 0 24px rgba(0,0,0,0.5);
        margin: 0 0 4px;
        line-height: 1;
        letter-spacing: 0.02em;
    }

    .hero-text p {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 1.2rem;
        color: rgba(255,255,255,0.92);
        margin: 0;
        text-shadow: 0 1px 4px rgba(0,0,0,0.8);
        max-width: 240px;
        line-height: 1.3;
    }

    .hero-action { flex-shrink: 0; }

    .unlock-wrap { position: relative; }

    .unlock-btn {
        display: flex !important;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 10px 22px !important;
        min-width: 100px;
        gap: 2px;
        height: auto !important;
    }

    .unlock-label {
        font-size: 1.2rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .unlock-price {
        font-size: 1.2rem;
        font-weight: 900;
    }

    /* ── Empty ── */
    .empty-state {
        padding: 48px 20px;
        text-align: center;
        color: var(--color-lightGrey, #e2e29b);
        font-family: var(--font-family-sans, sans-serif);
        opacity: 0.7;
    }

    /* ── Rows ── */
    .rows-list {
        display: flex;
        flex-direction: column;
        background: #f5ede0;
    }

    .reward-row {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        border-bottom: 1px solid rgba(0,0,0,0.09);
        animation: rowIn 0.35s ease-out both;
        background: #f7f0e2;
    }

    .reward-row:nth-child(even) { background: #f0e8d6; }
    .reward-row:last-child { border-bottom: none; }
    .reward-row.claimed { opacity: 0.55; }

    /* ── Icon frame ── */
    .row-icon {
        flex-shrink: 0;
        width: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .icon-frame {
        position: relative;
        width: 76px;
        height: 68px;
        border-radius: 10px;
        background: linear-gradient(145deg, #5a3a1a, #3a1a00);
        box-shadow: 0 3px 8px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: visible;
    }

    .icon-frame.icon-claimable {
        background: linear-gradient(145deg, #d4920e, #8b5e00);
        box-shadow: 0 0 14px rgba(210,140,10,0.55), 0 3px 8px rgba(0,0,0,0.4);
        animation: pulse 2s ease-in-out infinite;
    }

    .icon-frame.icon-claimed {
        background: linear-gradient(145deg, #4a8c4a, #2a6a2a);
    }

    .monster-img {
        width: 72px;
        height: 64px;
        object-fit: contain;
        border-radius: 8px;
    }

    .monster-img.img-locked {
        filter: brightness(0.4) grayscale(0.4);
    }

    .lock-overlay {
        position: absolute;
        bottom: -6px;
        right: -6px;
        width: 26px;
        height: 26px;
        object-fit: contain;
        filter: drop-shadow(0 1px 3px rgba(0,0,0,0.7));
        z-index: 2;
    }

    .claimed-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 44px;
        height: 44px;
        object-fit: contain;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
        z-index: 2;
    }

    /* ── Text ── */
    .row-text {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }

    .row-gold {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-weight: 700;
        font-size: 1.2rem;
        color: #2a1800;
        display: flex;
        align-items: center;
        gap: 5px;
    }

    .gold-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 25px;
        height: 25px;
        overflow: hidden;
        flex-shrink: 0;
        transform: scale(0.7);
        margin: -5px;
    }

    .gold-icon :global(i.gold_l) {
        transform: scale(0.38);
        transform-origin: center;
        flex-shrink: 0;
    }

    .row-req {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 1rem;
        color: #6b4a1a;
    }

    /* ── Action buttons ── */
    .row-action {
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
    }

    .action-btn {
        font-size: 1.1rem !important;
        padding: 0.7rem 1.4rem !important;
        height: auto !important;
        min-width: 90px;
        border-radius: 6px !important;
        line-height: 1.3 !important;
        margin-top: 0 !important;
    }

    .claimed-text {
        font-family: var(--font-family-sans-condensed, 'Fira Sans Condensed', sans-serif);
        font-size: 1.1rem;
        font-weight: 700;
        color: #6b4a1a;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        min-width: 90px;
        padding: 0.7rem 1.4rem;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
    }

    .btn-locked:disabled, .btn-locked:disabled:hover {
        background: linear-gradient(180deg, #c0b0a0 0%, #8a7a68 100%) !important;
        opacity: 1 !important;
        color: rgba(255,255,255,0.9) !important;
        text-shadow: 0 1px 1px rgba(0,0,0,0.3) !important;
        cursor: default !important;
    }

    .error-msg {
        font-size: 0.74rem;
        color: #c0392b;
        animation: fadeOut 3s forwards;
        text-align: right;
        max-width: 90px;
    }

    @keyframes rowIn {
        from { opacity: 0; transform: translateX(-10px); }
        to   { opacity: 1; transform: translateX(0); }
    }

    @keyframes pulse {
        0%, 100% { box-shadow: 0 0 14px rgba(210,140,10,0.55), 0 3px 8px rgba(0,0,0,0.4); }
        50%       { box-shadow: 0 0 24px rgba(210,140,10,0.85), 0 3px 8px rgba(0,0,0,0.4); }
    }

    @keyframes fadeOut {
        0%, 70% { opacity: 1; }
        100%    { opacity: 0; }
    }
</style>
